import React, {createContext, useContext, useMemo} from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {ProgressToolCall} from 'llm/chat-library/progress/ProgressTypes';
import {getProcedureToolId} from 'llm/procedure-tool-library/procedureToolTypes';
import {getProcedureTypeForStudyTypeAllowNull} from 'studies/TypedStudyUtils';

import type {
  ProcedureRunPlansMapping,
  ProcedureRunPlansMappingVariables,
} from './__generated/ProcedureRunPlansMapping';
import type {ProcedureRunsMapping, ProcedureRunsMappingVariables} from './__generated/ProcedureRunsMapping';
import type {StudyTypeMapping, StudyTypeMappingVariables} from './__generated/StudyTypeMapping';
import {PROCEDURE_RUN_PLANS_QUERY, PROCEDURE_RUNS_QUERY, STUDY_TYPE_QUERY} from './ProcedureToolMappingQuery';

export interface ProcedureToolMappingContextValue {
  isLoading: boolean;
  getProcedureType: (id: string) => ProcedureType | undefined;
}

const ProcedureToolMappingContext = createContext<ProcedureToolMappingContextValue | null>(null);

interface ProcedureToolMappingProviderProps {
  toolCalls: ReadonlyArray<ProgressToolCall>;
  children: React.ReactNode;
}

function useProcedureTypeMappingForToolCalls(
  toolCalls: ReadonlyArray<ProgressToolCall>
): ProcedureToolMappingContextValue {
  const {prpIds, studyIds, pcrIds} = useMemo(() => {
    const prp: Array<string> = [];
    const study: Array<string> = [];
    const pcr: Array<string> = [];
    for (const toolCall of toolCalls) {
      const id = getProcedureToolId(toolCall.tool, toolCall.args);
      if (id == null) {
        continue;
      }
      if (id.startsWith('prp_')) {
        prp.push(id);
      } else if (id.startsWith('stdy_')) {
        study.push(id);
      } else if (id.startsWith('pcr_')) {
        pcr.push(id);
      }
    }
    return {prpIds: [...new Set(prp)], studyIds: [...new Set(study)], pcrIds: [...new Set(pcr)]};
  }, [toolCalls]);

  const prpResult = useSkippableBenchlingQuery<ProcedureRunPlansMapping, ProcedureRunPlansMappingVariables>(
    PROCEDURE_RUN_PLANS_QUERY,
    {
      variables: {ids: prpIds},
      fetchPolicy: 'cache-first',
      skip: prpIds.length === 0,
    }
  );

  const studyResult = useSkippableBenchlingQuery<StudyTypeMapping, StudyTypeMappingVariables>(
    STUDY_TYPE_QUERY,
    {
      variables: {ids: studyIds},
      fetchPolicy: 'cache-first',
      skip: studyIds.length === 0,
    }
  );

  const pcrResult = useSkippableBenchlingQuery<ProcedureRunsMapping, ProcedureRunsMappingVariables>(
    PROCEDURE_RUNS_QUERY,
    {
      variables: {ids: pcrIds},
      fetchPolicy: 'cache-first',
      skip: pcrIds.length === 0,
    }
  );

  const isLoading =
    (prpIds.length > 0 && prpResult.type === QueryResultType.PENDING) ||
    (studyIds.length > 0 && studyResult.type === QueryResultType.PENDING) ||
    (pcrIds.length > 0 && pcrResult.type === QueryResultType.PENDING);

  const prpData = prpResult.type === QueryResultType.SUCCESS ? prpResult.data : undefined;
  const studyData = studyResult.type === QueryResultType.SUCCESS ? studyResult.data : undefined;
  const pcrData = pcrResult.type === QueryResultType.SUCCESS ? pcrResult.data : undefined;

  const procedureTypeById = useMemo((): Map<string, ProcedureType> => {
    const map = new Map<string, ProcedureType>();

    if (prpData) {
      for (const node of prpData.procedureRunPlans) {
        if (node.__typename !== 'ProcedureRunPlanNode') {
          continue;
        }
        const proxy = node.procedureFlowchartConfigVersionProxy;
        if (proxy?.__typename === 'ProcedureFlowchartConfigVersionProxyNode') {
          map.set(node.id, proxy.procedure.procedureType);
        }
      }
    }

    if (studyData) {
      for (const study of studyData.studies) {
        if (study.__typename !== 'StudyNode') {
          continue;
        }
        const procedureType = getProcedureTypeForStudyTypeAllowNull(study.studySchema.studyType);
        if (procedureType != null) {
          map.set(study.id, procedureType);
        }
      }
    }

    if (pcrData) {
      for (const run of pcrData.procedureRuns) {
        if (run.__typename !== 'ProcedureRunNode') {
          continue;
        }
        map.set(run.id, run.procedureType);
      }
    }

    return map;
  }, [prpData, studyData, pcrData]);

  return useMemo(
    (): ProcedureToolMappingContextValue => ({
      isLoading,
      getProcedureType: (id: string) => procedureTypeById.get(id),
    }),
    [isLoading, procedureTypeById]
  );
}

export function ProcedureToolMappingProvider({
  toolCalls,
  children,
}: ProcedureToolMappingProviderProps): JSX.Element {
  const contextValue = useProcedureTypeMappingForToolCalls(toolCalls);

  return (
    <ProcedureToolMappingContext.Provider value={contextValue}>
      {children}
    </ProcedureToolMappingContext.Provider>
  );
}

/**
 * Returns null if used outside of a ProcedureToolMappingProvider; consumers
 * should fall back to the generic display name in that case.
 */
export function useProcedureToolMappingOptional(): ProcedureToolMappingContextValue | null {
  return useContext(ProcedureToolMappingContext);
}
